import { useEffect, useRef, useState } from 'react'
import { Globe, Check } from 'lucide-react'
import { useLocale, SUPPORTED_LOCALES } from './i18n'

/**
 * Compact, registry-driven language switcher (Tailwind-styled, token-based).
 * Reusable across marketing chrome — used by the legacy homepage Navbar so
 * /pricing and other legacy pages can switch language. Reads/writes the global
 * marketing locale store via useLocale().
 */
export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { locale, setLocale, dict } = useLocale()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const active = SUPPORTED_LOCALES.find((l) => l.code === locale) ?? SUPPORTED_LOCALES[0]

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={dict.nav.language}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <Globe className="h-4 w-4" strokeWidth={1.5} />
        <span>{active.nativeLabel}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 min-w-[140px] rounded-xl border border-border bg-card p-1.5 shadow-lg"
        >
          {SUPPORTED_LOCALES.map((l) => {
            const selected = l.code === locale
            return (
              <button
                key={l.code}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  setLocale(l.code)
                  setOpen(false)
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors ${
                  selected ? 'bg-muted font-semibold' : 'hover:bg-muted/60'
                }`}
              >
                <span>{l.nativeLabel}</span>
                {selected && <Check className="h-3.5 w-3.5 text-sky-600" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
